/**
 * Server-side data for the stock `search` block — the archives' whole browse
 * layer.
 *
 * Volto SSRs the `listing` block (core registers `getAsyncData:
 * getListingBlockAsyncData` in config/Blocks.jsx) but NOT the `search` block:
 * the search block fetches from an effect inside `withQuerystringResults`, and
 * effects never run on the server. Every canonical browse container
 * (`/documents`, `/delegations`, `/glossary`, `/people`) and every `?preset=`
 * deep link renders exactly one search block, so before this hook those pages
 * server-rendered nothing at all — measured 2026-07-22: 0 `<tr>` rows and 0
 * deep document hrefs on every container across all three sites. Documents
 * were reachable only from the sitemap. The archives had no internal link
 * graph, which costs both crawl discovery and the anchor-text/PageRank signal
 * that tells a crawler what each document is about.
 *
 * The extension point is core's own: `blocksConfig.<type>.getAsyncData` is
 * called by `fetchContent` (components/theme/App/App.jsx) with the content it
 * has just loaded, and its returned promises are awaited before render — so
 * this costs no extra content request and needs no new route.
 *
 * The query built here must be the one the browser would build, or the first
 * client render would visibly swap the rows. The browser's chain is
 * `withPresetData -> withNormalizedData -> withSearch -> SearchBlockView`;
 * the two data transforms are reproduced below by calling the SAME functions.
 *
 * FACET STATE IS IN THE QUERY STRING, NOT THE HASH (corrected 2026-08-16)
 *
 * This comment used to say facet state was unreachable from the server because
 * core keeps it in the URL hash. That is wrong, and it is worth stating plainly
 * because it made a live defect look impossible. Core's hook is *named*
 * `useHashState` and *reads* both `location.search` and `location.hash` "to
 * maintain parameter compatibility" — but the setter is
 * `history.push({ search: qs.stringify(newParams) })`. It **writes to the query
 * string**. So every facet a visitor clicks mints a real, server-visible,
 * crawlable URL, and before this hook resolved them those URLs server-rendered
 * the *unfiltered* hub: identical rows, identical count, identical meta
 * description. See BL-0047.
 *
 * Facets are therefore resolved here, by `facetQuery.js`, which delegates the
 * value-to-criterion mapping to each facet widget's own `valueToQuery` so the
 * server and the client cannot drift.
 *
 * The free-text term is resolved here too (BL-0143). Before that, a
 * `/documents?SearchableText=` link rendered the unfiltered hub: the term was
 * echoed into the pager and the rows were page 1 of everything.
 */
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';

import { withQuerystringKey } from '../../helpers/querystringKey';
import { BROWSE_METADATA_FIELDS } from '../../helpers/browseFields';
import normalizeBlockData from '../../searchMap';
import { applyPreset, getPreset } from '../../presets';
import { siteIdFromEnv } from '../../siteConfig/branding';
import { baseFacetCriteria, facetCriteriaFrom, withFacets } from './facetQuery';
import {
  pageFrom,
  pageKeyFor,
  presetIdFrom,
  searchTextCriterion,
} from './browseState';

/**
 * The block data the browser will actually render: preset merged first
 * (withPresetData composes outermost), then legacy->unified normalization.
 */
export const resolveBlockData = (data, search, siteId) => {
  const preset = getPreset(siteId, data?.family, presetIdFrom(search));
  return normalizeBlockData(applyPreset(data, preset));
};

export default function getSearchBlockAsyncData({
  data,
  path,
  location,
  id,
  dispatch,
  content,
}) {
  const resolved = resolveBlockData(data, location?.search, siteIdFromEnv());
  // Facets from the URL override the same index in the block's base query; a
  // URL with no facet parameters leaves `query` the identical object.
  const urlQuery = withFacets(
    resolved?.query,
    facetCriteriaFrom(resolved, location?.search),
  );
  // Plus the rows the browser appends for facets the base query already
  // filters on (every directives series preset), so this request is the one
  // the hydration pass would make. See baseFacetCriteria.
  const seeded = baseFacetCriteria(resolved, location?.search);
  const query = seeded.length
    ? { ...urlQuery, query: [...(urlQuery?.query || []), ...seeded] }
    : urlQuery;

  // No criteria means core's applyDefaults would fall back to a path query we
  // cannot reproduce here; leave those blocks to the client rather than seed a
  // subrequest with the wrong result set.
  if (!query?.query?.length) return [Promise.resolve(null)];

  // The term replaces any SearchableText row the block already carries, as
  // core's normalizeState does on the client, so both render the same rows.
  const text = searchTextCriterion(location?.search);
  const searchQuery = text ? withFacets(query, [text]) : query;

  // The subrequest key is the BARE block id, not `${UID}-${id}`: SearchBlockView
  // renders <ListingBody> without a `properties` prop, so core's
  // withQuerystringResults falls through to `id`. (A plain listing block, which
  // IS handed `properties`, uses the prefixed form — hence core's helper differs
  // here.) Getting this wrong seeds a key nothing ever reads, and the page still
  // renders empty with no error.
  //
  // Stamped with its request key so that the hydration pass can recognise this
  // result as the answer to the request it is about to make, and not make it
  // (helpers/querystringKey, withQuerystringResults).
  return [
    dispatch(
      withQuerystringKey(
        getQueryStringResults(
          getBaseUrl(path || ''),
          {
            // `data.family` (documents|delegations|glossary|people) stamps
            // only the 12 canonical browse blocks, never the Search App
            // (config/searchApp.ts's getSearchAppData has no `family` key
            // and never reaches this hook — it has no content object to SSR
            // against). Narrowing `_all` to the columns TableListing.jsx
            // actually reads cut the response for each site's real, stored
            // `/documents` block query (b_size 25, no term) from 92,927 to
            // 33,385 bytes on directives (-64.1%), 113,661 to 43,380 on
            // standards (-61.8%), 103,711 to 37,434 on nnsa (-63.9%)
            // [127.0.0.1:9080 curl, 2026-09-27] — see helpers/browseFields.js
            // for which columns and why each one is there.
            metadata_fields: data?.family ? BROWSE_METADATA_FIELDS : '_all',
            ...searchQuery,
          },
          id,
          pageFrom(location?.search, pageKeyFor(content, id)),
        ),
      ),
    ),
  ];
}
